// ABOUTME: Decorates Horizon cart rows (page list + drawer) with the Recharge subscription
// glue — plan labels, in-cart plan swap/upsell, subscribed pricing, preset-bundle lines.

import { CartEvents } from '@theme/jf-cart-events';

// Live circular-arrows subscription icon (CartItem.jsx / cart-item.liquid, 16x18).
const SUBSCRIPTION_ICON =
  '<svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M5.00735 0.600708L7.09153 2.16289L7.98475 2.8324L7.31524 3.72562L5.75306 5.80979L4.85984 5.14029L5.99238 3.62932C3.25622 3.89059 1.11628 6.19539 1.11628 9C1.11628 11.9798 3.53186 14.3954 6.51163 14.3954V15.5116C2.91535 15.5116 0 12.5963 0 9C0 5.66928 2.5007 2.92263 5.72701 2.53516L4.33785 1.49393L5.00735 0.600708ZM10.273 15.4648L11.6622 16.5061L10.9926 17.3993L8.90847 15.8371L8.01525 15.1676L8.68476 14.2744L10.2469 12.1902L11.1402 12.8597L10.0076 14.3707C12.7438 14.1094 14.8837 11.8046 14.8837 9C14.8837 6.02023 12.4681 3.60465 9.48837 3.60465V2.48838C13.0846 2.48838 16 5.40373 16 9C16 12.3307 13.4993 15.0774 10.273 15.4648Z" fill="currentColor"/>' +
  '</svg>';

const ONE_TIME_LABEL = 'One-time purchase';

// Core components that mutate the cart and morph its own rows: the row stepper and remove
// button (cart-items-component) and every product-form add (product-form-component). Each
// settles the standard event's promise with its own source. The jf-cart operator's mutations
// are absent because they reach this element on the private cart:update bus instead.
const CORE_CART_SOURCES = new Set(['cart-items-component', 'product-form-component']);

// Live utils/helpers money(): all-zero decimals stripped ("$50" not "$50.00", "$49.98"
// unchanged). Grouped and in the shop's active currency, so a four-figure total reads the
// same here as in the money_without_trailing_zeros string the server renders beside it.
const money = (cents) =>
  new Intl.NumberFormat(window.Shopify?.locale || undefined, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);

// The percentage a line's plans save, as the upsell button and the plan menu's group label
// state it ("Subscribe & Save 10%"): the first plan's percentage adjustment, else 10.
const savingsPercent = (plans) => {
  const first = plans[0];
  const percent = first && first.adj_type === 'percentage' ? first.adj_value : null;
  return percent === null || percent === undefined ? 10 : percent;
};

/**
 * The one connected instance that owns the listeners, the row observer and the fetches. A
 * product template renders the glue from two hosts (the product section's block and the cart
 * drawer) and both payloads carry the same lines, so a second armed instance would only
 * duplicate every /cart.js and /products/<handle>.js request the first already makes.
 * @type {JfRechargeCart|null}
 */
let owner = null;

class JfRechargeCart extends HTMLElement {
  /** @type {Array<Object>} Freshest per-line Recharge data (SSR payload, then /cart.js refreshes). */
  #lines = [];

  /**
   * @type {Map<string, {plans: Object, prices: Object|null}>} Product handle -> plan options
   * per variant id (every selling plan the variant allocates, each with its group's name)
   * + variant base prices (cart.js carries only the allocated unit price, so the one-time
   * compare total needs the product's variant price).
   */
  #productCache = new Map();

  /** @type {Set<string>} Row keys a line-set recovery has already run for (one attempt each). */
  #recoveredKeys = new Set();

  connectedCallback() {
    // A second instance stands down entirely: no listeners, no observer, no fetch.
    if (owner && owner !== this && owner.isConnected) return;
    owner = this;

    // Payload children are not parsed yet when the opening tag connects during
    // initial HTML parsing — the SSR payload is read lazily in #decorate().
    this.#decorateSoon();
    document.addEventListener(CartEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('shopify:cart:lines-update', this.#onStandardLinesUpdate);
    // Core re-renders cart-items sections outside cartUpdate too (post-load
    // hydration refresh), which strips the decoration two ways: replacing rows
    // (childList) or morph-patching existing row nodes in place — the patch
    // removes injected children and the decorated flag without adding nodes,
    // so the flag attribute is watched too.
    this.#rowObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-jf-recharge-decorated', 'data-key'],
    });
  }

  disconnectedCallback() {
    if (owner !== this) return;
    owner = null;
    document.removeEventListener(CartEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('shopify:cart:lines-update', this.#onStandardLinesUpdate);
    this.#rowObserver.disconnect();

    // A stood-down sibling can still be on the page: hand the work over so a morph that
    // removed this element does not leave every row undecorated.
    for (const sibling of document.querySelectorAll('jf-recharge-cart')) {
      if (sibling !== this && sibling instanceof JfRechargeCart && sibling.isConnected) {
        sibling.connectedCallback();
        return;
      }
    }
  }

  // Decoration adds labels/controls and only ever SETS the decorated flag, so
  // neither the childList watch nor the attribute watch retriggers on our writes.
  #rowObserver = new MutationObserver((mutations) => {
    const isUndecoratedRow = (node) =>
      node instanceof HTMLElement &&
      ((node.matches('.cart-items__table-row[data-key]') && node.dataset.jfRechargeDecorated !== 'true') ||
        Array.from(node.querySelectorAll('.cart-items__table-row[data-key]')).some(
          (row) => row.dataset.jfRechargeDecorated !== 'true'
        ));
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (isUndecoratedRow(mutation.target)) {
          this.#decorateSoon();
          return;
        }
        continue;
      }
      for (const node of mutation.addedNodes) {
        if (isUndecoratedRow(node)) {
          this.#decorateSoon();
          return;
        }
      }
    }
  });

  // Cart changed anywhere (quantity, add, swap): section morphs rebuild the rows, so
  // refresh the line data from the cart and re-decorate. Runs twice — once right after
  // the event and once after the morph settles — decoration is idempotent per row.
  #onCartUpdate = () => {
    this.#refresh().then(() => {
      this.#decorateSoon();
      setTimeout(() => this.#decorate(), 600);
    });
  };

  // Core's own cart mutations (the quantity stepper, the remove button, a product-form add)
  // announce only the standard event and morph the rows themselves: refresh the line data once
  // such a mutation settles, so the re-decoration never binds a stale key or quantity. Every
  // other mutation reaches #onCartUpdate through the private bus and is not refreshed twice.
  #onStandardLinesUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        if (CORE_CART_SOURCES.has(detail?.source)) this.#onCartUpdate();
      })
      .catch(() => {});
  };

  // rAF settles after paint/morph, but hidden tabs never run rAF callbacks —
  // the timer covers those (decoration is idempotent per row).
  #decorateSoon() {
    requestAnimationFrame(() => this.#decorate());
    setTimeout(() => this.#decorate(), 250);
  }

  /**
   * @returns {Array<Object>} The server-rendered payload (exact Liquid values). Where the page
   * carries two, the drawer's copy wins: a cart morph re-renders that one, so it is the only
   * copy that can be fresher than the render this element was parsed with.
   */
  #ssrLines() {
    const payload =
      document.querySelector('.cart-drawer [data-jf-recharge-lines]') ||
      this.querySelector('[data-jf-recharge-lines]');
    if (!payload) return [];
    try {
      return JSON.parse(payload.textContent);
    } catch (error) {
      console.error('jf-recharge-cart: payload parse failed', error);
      return [];
    }
  }

  // Rebuilds #lines from /cart.js (+ /products/<handle>.js for plan options, cached),
  // mirroring the SSR payload shape.
  async #refresh() {
    try {
      const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.js`;
      const localeRoot = window.Shopify?.routes?.root || '/';
      const cart = await fetch(cartUrl).then((r) => r.json());
      const lines = [];
      for (const [index, item] of cart.items.entries()) {
        const cached = this.#productCache.get(item.handle);
        if (!cached || cached.prices === null) {
          try {
            const product = await fetch(`${localeRoot}products/${item.handle}.js`).then((r) => r.json());
            // Plan names and adjustments live on the groups; which plans apply to a variant
            // lives on the variant's allocations (selling_plan_id + selling_plan_group_id).
            const plansById = new Map();
            for (const group of product.selling_plan_groups || []) {
              for (const plan of group.selling_plans || []) {
                const adj = plan.price_adjustments[0];
                plansById.set(plan.id, {
                  id: plan.id,
                  name: plan.name,
                  adj_type: adj?.value_type,
                  adj_value: adj?.value,
                });
              }
            }
            const plans = {};
            const prices = {};
            for (const variant of product.variants || []) {
              prices[variant.id] = variant.price;
              const seen = new Set();
              plans[variant.id] = (variant.selling_plan_allocations || [])
                .map((allocation) => allocation.selling_plan_id)
                .filter((id) => plansById.has(id) && !seen.has(id) && seen.add(id))
                .map((id) => plansById.get(id));
            }
            this.#productCache.set(item.handle, { plans, prices });
          } catch (error) {
            // prices stays null so the next refresh retries: the guard above treats a non-null
            // prices as a complete entry, and a fetch that failed once holds no plan options.
            this.#productCache.set(item.handle, { plans: cached ? cached.plans : {}, prices: null });
          }
        }
        const entry = this.#productCache.get(item.handle);
        const basePrice = (entry.prices && entry.prices[item.variant_id]) || item.price;
        const allocation = item.selling_plan_allocation
          ? { id: item.selling_plan_allocation.selling_plan.id, name: item.selling_plan_allocation.selling_plan.name }
          : null;
        const properties = item.properties || {};
        lines.push({
          key: item.key,
          line: index + 1,
          handle: item.handle,
          variant_id: item.variant_id,
          quantity: item.quantity,
          variant_price: basePrice,
          final_line_price: item.final_line_price,
          compare_total: money(basePrice * item.quantity),
          allocation,
          plans: entry.plans[item.variant_id] || [],
          preset: properties['_preset_'] ?? null,
          rc_bundle: properties['_rc_bundle'] ?? null,
          bundle_product_id: properties['_bundleProductId'] ?? null,
        });
      }
      this.#lines = lines;
    } catch (error) {
      console.error('jf-recharge-cart: refresh failed', error);
    }
  }

  // Adopts the server-rendered payload as the line set and seeds the plan options it carries
  // (prices stay null so the next refresh fetches the variant base prices the payload omits).
  #readSsrLines() {
    this.#lines = this.#ssrLines();
    for (const line of this.#lines) {
      const seeded = this.#productCache.get(line.handle);
      this.#productCache.set(line.handle, {
        plans: { ...(seeded ? seeded.plans : {}), [line.variant_id]: line.plans },
        prices: null,
      });
    }
  }

  /**
   * Re-reads the payload, then the cart, for rows whose key this element has not seen. A core
   * add morphs the rows in without refreshing every payload that renders them (a product
   * template hosts the glue in its own section), so the row arrives with a key the current line
   * set cannot match. One recovery per key: a key the cart itself does not carry is a line that
   * is gone, and must not re-fetch on every decoration pass.
   * @param {Array<string>} keys - Row keys missing from #lines.
   */
  #recoverLines(keys) {
    const pending = keys.filter((key) => !this.#recoveredKeys.has(key));
    if (!pending.length) return;
    for (const key of pending) this.#recoveredKeys.add(key);

    const ssrLines = this.#ssrLines();
    if (pending.some((key) => ssrLines.some((line) => line.key === key))) {
      this.#readSsrLines();
      this.#decorate();
      return;
    }
    this.#refresh().then(() => this.#decorate());
  }

  #decorate() {
    if (!this.#lines.length) this.#readSsrLines();
    this.setAttribute('data-jf-lines', String(this.#lines.length));

    const unmatched = [];
    for (const row of document.querySelectorAll('.cart-items__table-row[data-key]')) {
      const data = this.#lines.find((line) => line.key === row.dataset.key);
      if (!data) {
        if (row.dataset.key) unmatched.push(row.dataset.key);
        continue;
      }
      this.#decorateRow(row, data);
    }

    if (unmatched.length) this.#recoverLines(unmatched);
  }

  /**
   * @param {HTMLElement} row - A core cart line row (page or drawer).
   * @param {Object} data - The line's Recharge payload entry.
   */
  #decorateRow(row, data) {
    if (row.dataset.jfRechargeDecorated === 'true') return;
    // A hydration morph can strip the flag while leaving injected nodes (or vice
    // versa) — clear leftover artifacts so re-decoration never doubles. The core
    // may render the plan-name <p> itself (the label render reuses it): that node
    // is unclassified, never removed.
    row.querySelectorAll('.jf-preset-line, .jf-select-wrap, .jf-upsell-button, .jf-compare, .jf-plan-label__icon').forEach((node) => node.remove());
    for (const label of row.querySelectorAll('.jf-plan-label')) {
      if (label.textContent.trim() === ONE_TIME_LABEL) label.remove();
      else label.classList.remove('jf-plan-label', 'jf-plan-label--sub');
    }
    row.dataset.jfRechargeDecorated = 'true';

    const inDrawer = Boolean(row.closest('.cart-drawer'));

    if (data.bundle_product_id) {
      row.classList.add('jf-bundle-item');
    }

    this.#renderPresetLines(row, data);
    this.#renderPlanLabel(row, data, inDrawer);

    if (data.plans.length > 0) {
      this.#renderSwapControl(row, data);
    }

    if (data.allocation) {
      this.#renderSubscribedPrice(row, data);
    }
  }

  // Line-stack insertion: Recharge controls sit ahead of the line's offer-chip row when it
  // carries one (the chips close the stack), else at the end of the wrapper.
  #insertRow(wrapper, node) {
    wrapper.insertBefore(node, wrapper.querySelector('.jf-cart-offer-chips'));
  }

  // Descriptor lines (plan name, one-time label) join the line's variants stack
  // (.jf-cart-line-variants, the drawer grid's `variants` item) when the row renders one;
  // a row without options, properties or a plan has no stack, so the line opens the wrapper.
  #insertVariantLine(wrapper, node) {
    const stack = wrapper.querySelector('.jf-cart-line-variants');
    if (stack) {
      stack.appendChild(node);
    } else {
      this.#insertRow(wrapper, node);
    }
  }

  // Live cart-item.liquid: one text-xsmall grey line per "_preset_" component.
  #renderPresetLines(row, data) {
    if (!data.preset) return;

    const info = row.querySelector('.cart-items__product-info');
    if (!info) return;

    for (const component of String(data.preset).split(',')) {
      const line = document.createElement('p');
      line.className = 'jf-preset-line';
      line.textContent = component;
      info.appendChild(line);
    }
  }

  // Live: subscribed -> red flex label with the circular-arrows icon + plan name;
  // otherwise "One-time purchase" (page: every line; drawer: plan-selling products only).
  #renderPlanLabel(row, data, inDrawer) {
    const wrapper = row.querySelector('.cart-items__variants-wrapper');
    if (!wrapper) return;

    if (data.allocation) {
      let label = Array.from(wrapper.querySelectorAll('p')).find(
        (node) => node.textContent.trim() === data.allocation.name
      );
      if (!label) {
        label = document.createElement('p');
        label.textContent = data.allocation.name;
        this.#insertVariantLine(wrapper, label);
      }
      label.classList.add('jf-plan-label', 'jf-plan-label--sub');
      const icon = document.createElement('span');
      icon.className = 'jf-plan-label__icon';
      icon.innerHTML = SUBSCRIPTION_ICON;
      label.prepend(icon);
      return;
    }

    if (inDrawer && data.plans.length === 0) return;

    const label = document.createElement('p');
    label.className = 'jf-plan-label';
    label.textContent = ONE_TIME_LABEL;
    this.#insertVariantLine(wrapper, label);
  }

  // Live's cart (Rebuy Smart Cart): subscribed lines swap plans through a select of two
  // groups, "Pay Full Price" holding "One-time only" and "Subscribe & Save X%" holding
  // every plan the line's variant allocates (option label = the plan's name as Recharge
  // names it) in ascending selling plan id, the order Rebuy's product feed lists them;
  // one-time lines upsell with the outline button into the first applicable plan.
  #renderSwapControl(row, data) {
    const wrapper = row.querySelector('.cart-items__variants-wrapper');
    if (!wrapper) return;

    if (data.allocation) {
      const selectWrap = document.createElement('div');
      selectWrap.className = 'jf-select-wrap';

      const select = document.createElement('select');
      select.className = 'jf-plan-select';
      select.setAttribute('aria-label', 'Purchase type');

      const oneTimeGroup = document.createElement('optgroup');
      oneTimeGroup.label = 'Pay Full Price';
      const oneTimeOption = document.createElement('option');
      oneTimeOption.value = 'one-time';
      oneTimeOption.textContent = 'One-time only';
      oneTimeGroup.appendChild(oneTimeOption);
      select.appendChild(oneTimeGroup);

      const planGroup = document.createElement('optgroup');
      planGroup.label = `Subscribe & Save ${savingsPercent(data.plans)}%`;
      for (const plan of [...data.plans].sort((a, b) => Number(a.id) - Number(b.id))) {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = plan.name;
        if (String(plan.id) === String(data.allocation.id)) {
          option.selected = true;
        }
        planGroup.appendChild(option);
      }
      select.appendChild(planGroup);
      // A line allocated to a plan the variant no longer offers still shows its own plan
      // (the select would otherwise fall back to One-time only while the line stays subscribed).
      if (!Array.from(select.options).some((option) => option.selected)) {
        const current = document.createElement('option');
        current.value = String(data.allocation.id);
        current.textContent = data.allocation.name;
        current.selected = true;
        select.appendChild(current);
      }

      select.addEventListener('change', () => {
        const value = select.value;
        this.#swap(row, value === 'one-time' ? null : value);
      });

      selectWrap.appendChild(select);
      this.#insertRow(wrapper, selectWrap);
      return;
    }

    const savings = savingsPercent(data.plans);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'jf-upsell-button';
    button.innerHTML = `<span class="jf-upsell-button__label">Subscribe &amp; Save ${savings}%</span>`;
    button.addEventListener('click', () => {
      this.#swap(row, data.plans[0].id);
    });

    this.#insertRow(wrapper, button);
  }

  // Live: subscribed price renders red with the one-time line total struck below.
  #renderSubscribedPrice(row, data) {
    const priceCell = row.querySelector('.cart-items__price');
    if (!priceCell) return;

    priceCell.classList.add('jf-price--sub');

    // Rows rendered by jf-cart-products carry the struck one-time total server-side
    // (.jf-cart-line-compare); only rows on core markup (drawer) need the JS strike —
    // one money renderer per surface.
    if (priceCell.querySelector('.jf-cart-line-compare')) return;

    const compare = document.createElement('s');
    compare.className = 'jf-compare';
    compare.textContent = data.compare_total;
    priceCell.appendChild(compare);
  }

  /**
   * Changes the line's selling plan in place through the jf-cart operator — the ONE
   * authoritative cart-mutation integration (conductor adjudication 2026-07-10). The
   * operator bundles the page's cart sections, morphs every cart surface (drawer and cart
   * page alike; the rows arrive re-keyed), and announces cart:update; this element's
   * cartUpdate listener + row observer re-decorate the rows. The row's data-key is the
   * freshest line identity, and the quantity is read from the cart at swap time (a change
   * request without a quantity leaves the line at 1, and the decorator's cached quantity may
   * predate a stepper change): a swap changes the plan and nothing else.
   * @param {HTMLElement} row - The row being swapped.
   * @param {string|number|null} sellingPlanId - Target plan id, or null for one-time.
   */
  async #swap(row, sellingPlanId) {
    const operator = window.JFCart;
    if (!operator || typeof operator.changeLine !== 'function') {
      console.error('jf-recharge-cart: jf-cart operator missing — swap unavailable');
      return;
    }

    row.classList.add('jf-swapping');

    try {
      const key = row.dataset.key;
      const cart = await operator.getCart();
      const line = cart.items.find((item) => item.key === key);
      if (!line) throw new Error(`line ${key} is not in the cart`);
      await operator.changeLine({ key, quantity: line.quantity, sellingPlan: sellingPlanId });
    } catch (error) {
      console.error('jf-recharge-cart: swap failed', error);
    } finally {
      row.classList.remove('jf-swapping');
    }
  }
}

if (!customElements.get('jf-recharge-cart')) {
  customElements.define('jf-recharge-cart', JfRechargeCart);
}
